import React, {useContext, useEffect, useState} from 'react';
import {View, FlatList, Platform} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {
  Menu,
  MenuOptions,
  MenuOption,
  MenuTrigger,
} from 'react-native-popup-menu';

import Header from '../../components/elements/Header';
import Text from '../../components/elements/Text';
import Divider from '../../components/elements/Divider';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES} from '../../utils/Constants';
import Button from '../../components/elements/Button';
import {fetchApi, PAGE_SIZE} from '../../utils/ApiHelper';
import {
  CONTACTS,
  DELETE_CONTACTS,
  HTTP_METHODS,
} from '../../utils/URLConstants';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import EditContact from '../EditContact';
import {tID} from '../../utils/TestIdHelper';
import CommonStyles from '../../utils/CommonStyles';
import {
  CloseIcon,
  EmptyContacts,
  LeftArrowIcon,
  SeparatorIcon,
  UserIcon,
  VerticalDotsIcon,
} from '../../utils/SvgIcons';

const MyContacts = ({route, navigation, onClose}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [contacts, setContacts] = useState([]);
  const [openNewContact, setOpenNewContact] = useState(false);
  const [selectedContactData, setSelectedContactData] = useState();
  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      const unsubscribe = navigation.addListener('focus', () => {
        // The screen is focused
        getContacts();
      });

      // Return the function to unsubscribe from the event so it gets removed on unmount
      return unsubscribe;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus-only data load
  }, [navigation]);

  useEffect(() => {
    if (Platform.OS === 'web' && !openNewContact) getContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch intentionally keyed to contact panel close only
  }, [openNewContact]);

  const getContacts = async () => {
    const params = {page: 1, size: PAGE_SIZE};
    lpc?.setVisibleWithTimeout(true);
    fetchApi(HTTP_METHODS.GET, CONTACTS, null, params)
      .then(response => {
        lpc?.setVisibleWithTimeout(false);
        if (response.status === 200) {
          setContacts(response.data.data.rows);
        }
      })
      .catch(error => {
        // Dlog(error);
        lpc?.setVisibleWithTimeout(false);
        esc.showError(error);
      });
  };

  const deleteContact = id => {
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.DELETE, DELETE_CONTACTS(id))
      .then(response => {
        if (response.status === 200) {
          getContacts();
        }
      })
      .catch(error => {
        // Dlog(error);
        lpc.setVisible(false);
        esc.showError(error);
      });
  };

  const onPressNewContact = () => {
    if (Platform.OS === 'web') {
      setSelectedContactData(undefined);
      setOpenNewContact(true);
    } else {
      navigation.navigate('EditContact');
    }
  };

  const renderListItem = ({item, index}) => {
    return (
      <View key={index}>
        <View style={styles.listItemContainer}>
          <View style={styles.nameContainer}>
            <UserIcon
              testID={tID('MyContacts-Image-User')}
              style={CommonStyles.mr_20}
            />
            <Text
              testID={tID('MyContacts-Text-Name')}
              variation="Subtitle1Regular"
              color={colors.textSecondary}>
              {item.firstname} {item.lastname}
            </Text>
          </View>

          <Menu>
            <MenuTrigger>
              <VerticalDotsIcon style={styles.optionIcon} />
            </MenuTrigger>
            <MenuOptions optionsContainerStyle={styles.menuOptionsContainer}>
              <MenuOption
                onSelect={() => {
                  navigation.navigate('EditContact', {contactData: item});
                }}
                customStyles={{
                  optionWrapper: {
                    paddingVertical: 16,
                    borderBottomColor: colors.border,
                    borderBottomWidth: 1,
                  },
                }}>
                <Text
                  testID={tID('MyContacts-Text-Edit')}
                  variation="Subtitle1Regular"
                  color={colors.textMuted}>
                  {Platform.OS === 'web' ? 'Edit' : 'Edit Contact'}
                </Text>
              </MenuOption>
              <MenuOption
                onSelect={() => deleteContact(item.id)}
                customStyles={{optionWrapper: {paddingVertical: 16}}}>
                <Text
                  testID={tID('MyContacts-Text-Delete')}
                  variation="Subtitle1Regular"
                  color={colors.textMuted}>
                  {Platform.OS === 'web' ? 'Delete' : 'Delete Contact'}
                </Text>
              </MenuOption>
            </MenuOptions>
          </Menu>
        </View>
        <Divider top={0} />
      </View>
    );
  };

  const renderWebListItem = ({item, index}) => {
    return (
      <div
        key={index}
        style={{...styles.listItem, ...(index === 0 && CommonStyles.mt_32)}}>
        <UserIcon testID={tID('MyContacts-img-User')} width={20} height={20} />
        <div style={styles.listText}>
          <Text
            testID={tID('MyContacts-Text-Name')}
            variation="Body1SemiBold"
            color={colors.textPrimary}>
            {item.firstname} {item.lastname}
          </Text>
          <Text
            testID={tID('MyContacts-Text-Phone')}
            variation="Text1SemiBold"
            color={colors.textSecondary}>
            {item.phone}
          </Text>
          <Text
            testID={tID('MyContacts-Text-Email')}
            variation="Text1SemiBold"
            color={colors.textSecondary}>
            {item.email}
          </Text>
          <div style={styles.listActions}>
            <Text
              testID={tID('MyContacts-Text-Edit')}
              variation="Text1SemiBold"
              color={colors.link}
              onPress={() => {
                setSelectedContactData({params: {contactData: item}});
                setOpenNewContact(true);
              }}>
              Edit
            </Text>{' '}
            <SeparatorIcon />
            <Text
              testID={tID('MyContacts-Text-Delete')}
              variation="Text1SemiBold"
              color={colors.link}
              onPress={() => {
                deleteContact(item.id);
              }}>
              Delete
            </Text>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyComponent = () => {
    return (
      <Text
        testID={tID('MyContacts-Text-None')}
        variation="Body1Italic"
        topMargin={16}
        color={colors.textSecondary}>
        None added.
      </Text>
    );
  };

  const NoContacts = () => {
    return (
      <div style={styles.emptyContacts}>
        <EmptyContacts
          testID={tID('MyContacts-img-Empty')}
          style={CommonStyles.mb_16}
        />
        <Text
          testID={tID('MyContacts-Text-NoContacts')}
          variation="Body1SemiBold"
          color={colors.textHeading}
          style={CommonStyles.mb_16}>
          No contacts added yet.
        </Text>
        <Button
          testID={tID('MyContacts-Button-New')}
          onPress={onPressNewContact}
          title={'Add New Contact'}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          style={CommonStyles.ph_16}
        />
      </div>
    );
  };

  return (
    <SafeAreaView
      testID={tID('MyContacts-SafeAreaView-Main')}
      style={[
        styles.container,
        Platform.OS === 'web' && CommonStyles.full_screen_height,
      ]}>
      <View
        testID={tID('MyContacts-View-HeaderContainer')}
        style={[styles.headerContainer, {marginTop: 30}]}>
        <Header
          title={'My Contacts'}
          leftImage={Platform.OS === 'web' ? <CloseIcon /> : <LeftArrowIcon />}
          onPressLeftIcon={() => {
            if (Platform.OS !== 'web') {
              navigation.goBack();
              route?.params?.onRequestOpen && route?.params?.onRequestOpen();
            } else {
              onClose(false);
            }
          }}
        />
      </View>
      {!openNewContact ? (
        <View style={CommonStyles.ph_24}>
          <Text
            testID={tID('MyContacts-Text-SubTitle')}
            variation={Platform.OS === 'web' ? 'Text1SemiBold' : 'Text1Regular'}
            topMargin={24}
            color={Platform.OS === 'web' ? colors.textTertiary : colors.textSecondary}>
            Add your family, friends and doctors to your personal contact list
            at Diathrive Health.
          </Text>
          {Platform.OS === 'web' ? <hr style={styles.line} /> : null}
        </View>
      ) : null}
      {Platform.OS !== 'web' ? (
        <View style={styles.contentContainer}>
          <Text variation="Body1SemiBold" topMargin={24} color={colors.textSecondary}>
            Profile
          </Text>
          <FlatList
            data={contacts}
            keyExtractor={(item, index) => index}
            renderItem={renderListItem}
            ListEmptyComponent={renderEmptyComponent}
            style={styles.container}
          />
          <Button
            testID={tID('MyContacts-Button-New')}
            onPress={onPressNewContact}
            title={'New Contact'}
            buttonType={BUTTON_TYPES.Filled}
            titleColor={colors.textOnAccent}
            style={[styles.buttonContainer, {marginBottom: 24}]}
          />
        </View>
      ) : openNewContact ? (
        <EditContact
          route={selectedContactData}
          navigation={navigation}
          setOpenNewContact={setOpenNewContact}
        />
      ) : (
        <>
          <FlatList
            data={contacts}
            keyExtractor={(item, index) => index}
            renderItem={renderWebListItem}
            ListEmptyComponent={NoContacts}
            style={[styles.container]}
            contentContainerStyle={
              !contacts.length && styles.noContactsListContainer
            }
          />
          {contacts.length ? (
            <Button
              testID={tID('MyContacts-Button-New')}
              onPress={onPressNewContact}
              title={'Add New Contact'}
              buttonType={BUTTON_TYPES.Filled}
              titleColor={colors.textOnAccent}
              style={styles.webButtonContainer}
            />
          ) : null}
        </>
      )}
    </SafeAreaView>
  );
};

export default MyContacts;
